import React, { useCallback, useRef, useState } from "react";

import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import styles from "./SearchableSelector.module.css";

interface FilterProps<T> {
	triggerClassName?: string;
	triggerContent: string | React.ReactElement;
	searchParam: string;
	searchPlaceholder?: string;
	onSearch?(query: string): void;
	onClear?(): void;
	hideClearFilter?: boolean;
	value?: T;
	clearText: string;
}

export default function SearchableSelector<T>({
	triggerClassName,
	triggerContent,
	searchPlaceholder,
	onSearch,
	children,
	onClear,
	value,
	clearText,
}: React.PropsWithChildren<FilterProps<T>>) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);
	const [search, setSearch] = useState<string>("");
	const inputRef = useRef<HTMLInputElement>(null);

	const onTextFieldKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		event.stopPropagation();
	}, []);

	const onInputFocus = useCallback(() => {
		inputRef.current?.focus();
	}, [inputRef]);

	const setValue = useCallback(
		(value: string) => {
			setSearch(value);
			onSearch?.(value);
		},
		[onSearch],
	);

	return (
		<Menu open={isOpen} onOpenChange={toggleOpen}>
			<Menu.Trigger>
				<button
					className={cx(styles.trigger, triggerClassName, {
						[styles.active]: isOpen,
					})}
				>
					{triggerContent}
					<FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
				</button>
			</Menu.Trigger>
			<Menu.Content onFocus={onInputFocus}>
				{onSearch && (
					<>
						<div className={styles.search}>
							<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />
							<TextField
								value={search}
								setValue={setValue}
								onKeyDown={onTextFieldKeyDown}
								ref={inputRef}
								placeholder={searchPlaceholder ?? "Search"}
								autoFocus
								className={styles.searchInput}
							/>
						</div>
						<Menu.Separator />
					</>
				)}
				{children}
				<Menu.Separator />
				<Menu.Item onSelect={onClear} className={cx(styles.filter, { [styles.clear]: !!value })} disabled={!value}>
					{clearText}
				</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
